import { Box, Chip, Tooltip, Typography } from "@mui/material"
import { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2Term } from "./sstv2Vocabulary"

/**
 * Renderers for a `Sstv2Term` — the shared way this page attaches a plain-English
 * gloss to a piece of jargon (DEV-193, D37 pass).
 *
 * ## Two jobs
 *
 *  1. **Every jargon token gets its explanation on the screen it appears on.** The
 *     migration panels render backend enum values (`DRAINING_HISTORY`) and internal
 *     counter names (`adopted`, `alreadyFlipped`) directly. An operator should not
 *     have to find the architecture doc mid-incident to act on one.
 *  2. **An unmodelled value is shown as unknown, WITH its raw value.** This is the
 *     invariant the whole vocabulary layer exists for: when the backend ships a
 *     state this build does not know, the screen must say so and print the value,
 *     rather than fall through to a plausible-looking default. See the extension
 *     point in `./sstv2Vocabulary`.
 *
 * ## Accessibility
 *
 * A tooltip alone is mouse-only, so each term is focusable (`tabIndex={0}`) and
 * carries an `aria-label` holding label AND gloss — a screen reader gets the
 * explanation without needing the hover. The dotted underline is the visual affordance
 * that there is something to read; it is a border, not a colour, so it survives both
 * themes and does not lean on hue to carry meaning.
 */

/** Label + gloss + optional decision id, as one accessible string. */
const accessibleName = (
  label: string,
  gloss: string,
  decision?: string,
): string => (decision ? `${label}. ${gloss} (${decision})` : `${label}. ${gloss}`)

/**
 * Tooltip body. The decision id is a *suffix*, deliberately terse: it is a pointer
 * into `dev/projects/sl2-architecture.md` for a curious operator, not a substitute
 * for the gloss, and the long form is never pasted into the UI.
 */
const GlossBody: FC<{ gloss: string; decision?: string; raw?: string }> = ({
  gloss,
  decision,
  raw,
}) => (
  <Box>
    <Typography variant="caption" component="div">
      {gloss}
    </Typography>
    {raw && (
      <Typography
        variant="caption"
        component="div"
        fontFamily="monospace"
        sx={{ mt: 0.5, opacity: 0.85 }}
      >
        {raw}
      </Typography>
    )}
    {decision && (
      <Typography
        variant="caption"
        component="div"
        sx={{ mt: 0.5, opacity: 0.7 }}
      >
        {decision}
      </Typography>
    )}
  </Box>
)

/**
 * Resolve a term to the strings actually rendered.
 *
 * For an UNKNOWN term the label becomes "Unknown (RAW)" — the raw value is promoted
 * into the visible label rather than hidden in the tooltip, because the operator
 * reporting the problem needs to be able to read the value off the screen.
 */
export const useTermText = (term: Sstv2Term) => {
  const { t } = useTranslation()
  const base = t(term.labelKey)
  const label = term.known ? base : `${base} (${term.raw})`
  const gloss = t(term.glossKey)
  return {
    label,
    gloss,
    // The raw value is echoed into the tooltip only when it is NOT already in the
    // label, so a known term still exposes the backend value it maps to.
    tooltipRaw: term.known ? term.raw : undefined,
    ariaLabel: accessibleName(label, gloss, term.decision),
  }
}

/** A text label with a dotted underline, a tooltip gloss and a keyboard stop. */
export const Sstv2TermLabel: FC<{
  term: Sstv2Term
  /** Override the visible text (e.g. a counter's number) while keeping the gloss. */
  children?: ReactNode
  variant?: "body2" | "caption"
  color?: string
}> = ({ term, children, variant = "body2", color = "text.secondary" }) => {
  const { label, gloss, tooltipRaw, ariaLabel } = useTermText(term)
  return (
    <Tooltip
      title={
        <GlossBody gloss={gloss} decision={term.decision} raw={tooltipRaw} />
      }
    >
      <Typography
        variant={variant}
        color={color}
        component="span"
        tabIndex={0}
        aria-label={ariaLabel}
        sx={{
          borderBottom: "1px dotted",
          borderColor: "divider",
          cursor: "help",
        }}
      >
        {children ?? label}
      </Typography>
    </Tooltip>
  )
}

/**
 * A chip carrying a term.
 *
 * An unknown term is forced to the neutral `default` colour whatever the caller
 * asked for: colour is a claim about severity, and this build has no basis for one
 * about a state it does not model. Neutral-plus-raw-value is the honest rendering.
 */
export const Sstv2TermChip: FC<{
  term: Sstv2Term
  color?: "default" | "info" | "primary" | "success" | "error"
  size?: "small" | "medium"
}> = ({ term, color = "default", size = "small" }) => {
  const { label, gloss, tooltipRaw, ariaLabel } = useTermText(term)
  return (
    <Tooltip
      title={
        <GlossBody gloss={gloss} decision={term.decision} raw={tooltipRaw} />
      }
    >
      <Chip
        size={size}
        variant="outlined"
        color={term.known ? color : "default"}
        label={label}
        tabIndex={0}
        aria-label={ariaLabel}
        sx={{ cursor: "help" }}
      />
    </Tooltip>
  )
}
